/// <amd-module name="Wit/QueriesHub/ResultsView/QueryResultCSVExporter" />

import { format } from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssHistoryService } from "VSS/Platform/History";
import { IVssTelemetryService } from "VSS/Platform/Telemetry";
import { CoreFieldRefNames } from "Wit/Common/Generated/Constants";
import { IQueryService } from "Wit/Common/QueryService/IQueryService";
import { reportError } from "Wit/Common/QueryUtils/ErrorHandlingUtils";
import { TelemetryArea } from "Wit/Common/QueryUtils/QueriesHubConstants";
import { showConfirmationDialog } from "Wit/Common/WorkItemDialogs/Components/ConfirmationDialog";
import * as Resources from "Wit/QueriesHub/Resources";
import { PageSizes } from "Wit/WorkItemModel/OM/";
import { IQueryResult, IQueryResultItem } from "../Common/Interfaces";
import { QueryUtils } from "../Common/Utils/QueryUtils";

export interface IIndentationsData {
    maxIndentation: number;
    indentationsMap: { [id: number]: number };
}

const QueryResultLimit: number = 20000;
const newlineChar = "\n";
const commaDelimiter = ",";
const CSVFileName: string = "data.csv";
const ExportTelemetryArea: string = "QueryCSVExport";

export class QueryResultCSVExporter {
    private _queryService: IQueryService;
    private _queryResult: IQueryResult | undefined;
    private _telemetryService: IVssTelemetryService;
    private _pageContext: IVssPageContext;
    private _projectId: string;
    private _queryResultItems: IQueryResultItem[] | undefined;

    constructor(_pageContext: IVssPageContext, _projectId: string, _queryResultItems: IQueryResultItem[] | undefined) {
        this._pageContext = _pageContext;
        this._queryService = _pageContext.getService<IQueryService>("IQueryService");
        this._telemetryService = _pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
        this._queryResult = this._queryService.queryResult.value?.data;
        this._projectId = _projectId;
        this._queryResultItems = _queryResultItems;
    }

    public async downloadCSV(): Promise<void> {
        this._telemetryService.publishEvent("WIT", TelemetryArea, { action: "ExportButtonClicked" });
        if (!this._pageContext) {
            return;
        }

        if (!this._queryService.allFields.ready.value) {
            await this._queryService.initializeFields(this._projectId);
        }

        const fileName = this._queryService.query.value?.name ? this._queryService.query.value.name + ".csv" : CSVFileName;

        const historyService = this._pageContext.getService<IVssHistoryService>("IVssHistoryService");
        const routeValues = historyService.getState();
        if (!this._queryResultItems && routeValues["importData"] === "csv") {
            // if it's importWorkItems provider and it hasn't imported yet, use empty dataset
            this._queryResult = {} as IQueryResult;
        } else if (!this._queryResult) {
            this._queryResult = {
                targetIds: this._queryResultItems?.map(item => item.value["System.Id"])
            } as IQueryResult;
        }

        const data = await this._getCSVContent();
        if (data && data.length > 0) {
            const blob = new Blob(["\ufeff" + data], { type: "text/csv;charset=utf-8;" });

            const a = window.document.createElement("a");
            const blobUrl = window.URL.createObjectURL(blob);
            a.href = blobUrl;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(blobUrl); // Dispose blob
            this._telemetryService.publishEvent("WIT", ExportTelemetryArea, { action: "ExportSuccessful" });
        } else {
            showConfirmationDialog(this._pageContext, Resources.ExportToCsvNoQueryResultsMessage, Resources.OK, Resources.ExportToCsvCommand);
        }
    }

    private _getCSVContent(): Promise<string> | undefined {
        if (!this._queryResult || !this._queryResult.targetIds || this._queryResult.targetIds.length == 0) {
            return;
        }

        if (this._queryResult.targetIds.length > QueryResultLimit) {
            reportError(
                this._pageContext,
                TelemetryArea,
                format(Resources.ExportToCsvQueryResultTooBig, QueryResultLimit)
            );
            return;
        }

        return this._convertQueryResultToCSV(this._queryResult);
    }

    private async _convertQueryResultToCSV(queryResultsModel: IQueryResult): Promise<string> {
        const isTreeQuery: boolean = queryResultsModel.isTreeQuery ?? false;
        const isLinkQuery: boolean = queryResultsModel.isLinkQuery ?? false;

        // Id always 1st column
        const mandatoryRefNames = [CoreFieldRefNames.Id, CoreFieldRefNames.Title, CoreFieldRefNames.WorkItemType, CoreFieldRefNames.State];
        const mandatoryColNames = mandatoryRefNames.map(
            refName => this._queryService.allFields.value.find(field => field.referenceName === refName)?.name ?? ""
        );

        const refNames = QueryUtils.insertToFrontIfNotFound(this._queryService.queryColumns.value, mandatoryRefNames);
        const colNames = QueryUtils.insertToFrontIfNotFound(
            this._queryService.queryColumns.value.map(
                name => this._queryService.allFields.value.find(field => field.referenceName === name)?.name ?? ""
            ),
            mandatoryColNames
        );
        const idIndex = refNames.indexOf(CoreFieldRefNames.Id);

        const targetIds = [...new Set(queryResultsModel.targetIds)];
        const sourceIds = isTreeQuery ? queryResultsModel.sourceIds : []; // Link Query therefore returned as flat list

        await this._queryService.fetchWorkItems(targetIds);

        const promises = [];
        // Populate array with paged rows of escaped content
        for (let i = 0; i < targetIds.length; i += PageSizes.QUERY) {
            const subsetTargetIds = targetIds.slice(i, i + PageSizes.QUERY).filter(id => id > 0); // export only saved work items
            promises.push(this._getPagedCSVRows(subsetTargetIds, refNames, idIndex));
        }

        // If query type is direct links/1-hop, we warn the user that it'll be flattened
        if (isLinkQuery && !isTreeQuery) {
            const alertText = format(Resources.DirectLinksFlattenedMessage);
            showConfirmationDialog(this._pageContext, alertText, Resources.OK, Resources.WarningExportingDirectLinksTitle);
        }
        const csvRowsArr = await Promise.all(promises);

        csvRowsArr.unshift(Array(colNames));

        const indexToSplitOn = refNames.indexOf(CoreFieldRefNames.Title); // Must not be -1
        const indentationsData: IIndentationsData = this._getIndentationsData(targetIds, sourceIds);

        const csvTexts = this._getCSVTextsFromRows(csvRowsArr, idIndex, indexToSplitOn, indentationsData);

        // Return filtered, concatenated CSV string
        return csvTexts.filter(csvText => csvText.length > 0).join(newlineChar);
    }

    // Construct the CSV string
    private _getCSVTextsFromRows(csvRowsArr: string[][], idIndex: number, indexToSplitOn: number, indentationsData: IIndentationsData): string[] {
        let foundError = false;

        const maxIndentation = indentationsData.maxIndentation;
        const indentationsMap = indentationsData.indentationsMap;

        const csvTexts = csvRowsArr.map((rows, index) => {
            if (rows.length === 0) {
                foundError = true;
                return "";
            }

            let csvText = "";

            // Form the CSV string while reconciling the different indentations with the maximum one
            for (let i = 0; i < rows.length; i++) {
                const isColHeaderRow = index == 0 && i == 0;
                const id = Number(rows[i][idIndex].replace(/"/g, ""));
                const indentation = indentationsMap[id];

                // Append 'prefix'
                for (let j = 0; j < indexToSplitOn; j++) {
                    csvText += rows[i][j] + commaDelimiter;
                }

                // Handle indentation
                for (let j = 0; j <= maxIndentation; j++) {
                    if (isColHeaderRow) {
                        csvText += rows[i][indexToSplitOn] + (maxIndentation > 0 ? " " + String(j + 1) : ""); // ie. Title 1, Title 2, ...
                    } else if (j === indentation) {
                        csvText += rows[i][indexToSplitOn]; // Place field value in correct space
                    }

                    csvText += j < maxIndentation || indexToSplitOn < rows[i].length - 1 ? commaDelimiter : "";
                }

                // Append 'suffix'
                for (let j = indexToSplitOn + 1; j < rows[i].length; j++) {
                    csvText += rows[i][j] + (j < rows[i].length - 1 ? commaDelimiter : "");
                }

                csvText += i < rows.length - 1 ? newlineChar : "";
            }

            return csvText;
        });

        // Show error status for incomplete results
        if (foundError) {
            reportError(this._pageContext, ExportTelemetryArea, Resources.ExportToCsvError);
        }

        return csvTexts;
    }

    private async _getPagedCSVRows(targetIds: number[], refNames: string[], idIndex: number): Promise<any[]> {
        if (targetIds.length <= 0) {
            return [];
        }
        const indicesMap: { [id: number]: number } = this._getIndicesMap(targetIds);

        const rows = this._getRowsFromWorkItemIds(targetIds, refNames);

        // Rearrange rows back to original order
        const newRows = new Array(targetIds.length);
        if (rows) {
            rows.forEach((row, index) => {
                const id = row[idIndex];
                const origIndex = indicesMap[id];
                newRows[origIndex] = rows[index];
            });
        }
        return this._getEscapedCSVRows(newRows, refNames);
    }

    // Change server work items to sorted query rows
    private _getRowsFromWorkItemIds(targetIds: number[], refNames: string[]): any[] {
        const rows: any[] = [];

        const idField = this._queryService.allFields.value.find(field => field.referenceName === CoreFieldRefNames.Id);

        for (const targetId of targetIds) {
            const row = [];
            const queryItem =
                idField && this._queryService.queryResultItems.value.find(listItem => listItem.value[CoreFieldRefNames.Id] === targetId);

            for (const refName of refNames) {
                const field = this._queryService.allFields.value.find(field => field.referenceName === refName);

                if (field) {
                    const queryRowValue = queryItem && queryItem.value[field.referenceName];

                    if (field.isIdentity && queryRowValue && queryRowValue.distinctDisplayName !== undefined) {
                        row.push(queryRowValue.distinctDisplayName);
                    } else {
                        row.push(queryRowValue);
                    }
                }
            }
            rows.push(row);
        }
        return rows;
    }

    // Create dictionary for original order lookup - assumes that targetIds has no duplicates
    private _getIndicesMap(targetIds: number[]): { [id: number]: number } {
        const result: { [id: number]: number } = {};

        targetIds.forEach((targetId, index) => {
            result[targetId] = index;
        });

        return result;
    }

    // Creates dictionary for indentation level - assumes that targetIds has no duplicates
    private _getIndentationsData(targetIds: number[], sourceIds: number[]): IIndentationsData {
        const indentationsData: IIndentationsData = {
            maxIndentation: 0,
            indentationsMap: {}
        };

        targetIds.forEach((targetId, index) => {
            let indentation = 0;
            if (sourceIds.length > 0 && sourceIds[index] !== 0) {
                indentation = indentationsData.indentationsMap[sourceIds[index]] + 1; // Must exist due to tree structure
            }
            indentationsData.indentationsMap[targetId] = indentation;
            indentationsData.maxIndentation = Math.max(indentationsData.maxIndentation, indentation);
        });

        return indentationsData;
    }

    private _getEscapedCSVRows(rows: unknown[][], refNames: string[]): string[] {
        if (!rows || rows.length === 0) {
            return [];
        }

        let result: any[] = [];

        for (let i = 0; i < rows.length; i++) {
            if (!(rows[i] instanceof Array)) {
                continue; // Could not find work item due to invalid WI ID.
            }
            const escapedRow = new Array(refNames.length);
            for (let j = 0; j < refNames.length; j++) {
                escapedRow[j] = this._escapeForCSV(rows[i][j]);
            }
            result.push(escapedRow);
        }

        return result;
    }

    private _escapeForCSV(obj: unknown): string {
        if (typeof obj === "boolean") {
            return obj ? "1" : "0";
        } else {
            // If obj is a datetime string, use local time and use "d/m/yyyy hh:mm:ss AM/PM" format (for example: 1/7/2020 11:33:29 AM)
            let result = "";
            if (obj instanceof Date) {
                const dateobj = obj as Date;

                // Not using toLocaleString() because it adds a comma between the date and string.
                // Excel will automatically identify this date time format if there is no comma, making it easier for the user to modify the date formatting within Excel.
                obj = `${dateobj.toLocaleDateString()} ${dateobj.toLocaleTimeString()}`;
            }
            const str = obj == null ? "" : String(obj).replace(/"/g, '""');
            if (str !== "") {
                result += `\"${str}\"`;
            }
            return result;
        }
    }
}
